import { useState } from 'react';
import { Button } from '../../components/Button/Button';
import { Card } from '../../components/Card/Card';
import { ConfirmModal } from '../../components/ConfirmModal';
import { GoalCard } from './GoalCard';
import { OpsparingPrMaanedTable } from './OpsparingPrMaanedTable';
import { DepotMaanedTable } from './DepotMaanedTable';
import { CreateGoalModal } from './CreateGoalModal';
import { useUiStore } from '../../store/ui';
import {
  useCreateDepot,
  useCreateGoal,
  useDeleteDepot,
  useDeleteGoal,
  useUpdateDepot,
  useUpdateGoal,
  useYearBundle,
} from '../../store/queryHooks';
import { kr } from '../../lib/format';
import styles from './MaalPage.module.css';

type DeleteTarget =
  { kind: 'goal'; id: number; name: string } | { kind: 'depot'; id: number; name: string };

// Latest non-null amount across the whole year — "sidste indtastede" beløb,
// uanset om det ligger før eller efter den valgte måned.
function latestAny(amounts: (number | null)[]): number {
  for (let m = amounts.length - 1; m >= 0; m--) {
    const x = amounts[m];
    if (x !== null && x !== undefined) return x;
  }
  return 0;
}

export function MaalPage() {
  const { year, month, setToast } = useUiStore();
  const bundle = useYearBundle(year);
  const createGoal = useCreateGoal(year);
  const updateGoal = useUpdateGoal(year);
  const deleteGoal = useDeleteGoal(year);
  const createDepot = useCreateDepot(year);
  const updateDepot = useUpdateDepot(year);
  const deleteDepot = useDeleteDepot(year);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<DeleteTarget | null>(null);

  if (bundle.isPending) return <div>Indlæser…</div>;
  if (bundle.isError || !bundle.data) return <div>Kunne ikke hente året.</div>;

  const goals = bundle.data.goals;
  const depots = bundle.data.depots;
  const goalsTotal = goals.reduce((s, g) => s + latestAny(g.amounts), 0);
  const depotsTotal = depots.reduce((s, d) => s + latestAny(d.amounts), 0);
  const combinedTotal = goalsTotal + depotsTotal;

  async function onCreate(input: {
    name: string;
    target: number | null;
    start_amount: number | null;
  }) {
    try {
      await createGoal.mutateAsync({
        name: input.name,
        target: input.target,
        start_amount: input.start_amount ?? undefined,
      });
      setToast('Opsparing oprettet: ' + input.name);
    } catch {
      setToast('Kunne ikke oprette opsparing');
    }
  }

  async function onCreateInline() {
    try {
      await createGoal.mutateAsync({ name: 'Ny opsparing', target: null, start_amount: 0 });
      setToast('Opsparing tilføjet');
    } catch {
      setToast('Kunne ikke oprette opsparing');
    }
  }

  async function onRenameGoal(id: number, name: string) {
    try {
      await updateGoal.mutateAsync({ id, name });
    } catch {
      setToast('Kunne ikke gemme navn');
    }
  }

  function requestDeleteGoal(id: number, name: string) {
    setConfirmDelete({ kind: 'goal', id, name });
  }

  function requestDeleteDepot(id: number, name: string) {
    setConfirmDelete({ kind: 'depot', id, name });
  }

  async function performDelete() {
    if (!confirmDelete) return;
    try {
      if (confirmDelete.kind === 'goal') {
        await deleteGoal.mutateAsync(confirmDelete.id);
        setToast('Opsparing slettet');
      } else {
        await deleteDepot.mutateAsync(confirmDelete.id);
        setToast('Depot slettet');
      }
    } catch {
      setToast(
        confirmDelete.kind === 'goal' ? 'Kunne ikke slette opsparing' : 'Kunne ikke slette depot',
      );
    } finally {
      setConfirmDelete(null);
    }
  }

  async function onAddDepot() {
    try {
      await createDepot.mutateAsync({ name: 'Nyt depot', sub: 'Manuel', badge: 'D' });
      setToast('Depot tilføjet');
    } catch {
      setToast('Kunne ikke tilføje depot');
    }
  }

  async function onRenameDepot(id: number, name: string) {
    const d = depots.find((x) => x.id === id);
    if (!d || d.name === name) return;
    try {
      await updateDepot.mutateAsync({ id, name });
    } catch {
      setToast('Kunne ikke gemme navn');
    }
  }

  async function onEditDepotAmount(id: number, m: number, next: number | null) {
    const d = depots.find((x) => x.id === id);
    if (!d) return;
    const amounts = d.amounts.slice();
    amounts[m] = next;
    try {
      await updateDepot.mutateAsync({ id, amounts: amounts as (number | null)[] });
    } catch {
      setToast('Kunne ikke gemme beløb');
    }
  }

  async function onEditAmount(goalId: number, m: number, next: number | null) {
    const g = goals.find((x) => x.id === goalId);
    if (!g) return;
    const amounts = g.amounts.slice();
    amounts[m] = next;
    try {
      await updateGoal.mutateAsync({ id: goalId, amounts: amounts as (number | null)[] });
    } catch {
      setToast('Kunne ikke gemme beløb');
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.kpiRow}>
        <div className={`${styles.kpi} ${styles.kpiAccent}`}>
          <div className={styles.kpiLabel}>Samlet opsparing (opsparing + aktier)</div>
          <div className={`${styles.kpiValue} ${styles.kpiValueLarge}`}>{kr(combinedTotal)}</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Opsparing</div>
          <div className={styles.kpiValue}>{kr(goalsTotal)}</div>
        </div>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Aktier &amp; depoter</div>
          <div className={styles.kpiValue}>{kr(depotsTotal)}</div>
        </div>
      </div>

      <section className={styles.section}>
        <div className={styles.head}>
          <h2 className={styles.title}>Opsparing</h2>
        </div>
        <div className={styles.grid}>
          {goals.map((g) => (
            <GoalCard
              key={g.id}
              goal={g}
              onDelete={() => requestDeleteGoal(g.id, g.name)}
            />
          ))}
        </div>
      </section>

      <Card>
        <OpsparingPrMaanedTable
          year={year}
          monthIdx={month}
          goals={goals}
          onEditAmount={onEditAmount}
          onCreateInline={onCreateInline}
          onRenameGoal={onRenameGoal}
          onDeleteGoal={requestDeleteGoal}
        />
      </Card>

      <Card>
        <DepotMaanedTable
          year={year}
          monthIdx={month}
          depots={depots}
          onAdd={onAddDepot}
          onRename={onRenameDepot}
          onDelete={requestDeleteDepot}
          onEditAmount={onEditDepotAmount}
        />
      </Card>

      {showCreate && <CreateGoalModal onClose={() => setShowCreate(false)} onCreate={onCreate} />}

      {confirmDelete && (
        <ConfirmModal
          title={confirmDelete.kind === 'goal' ? 'Slet opsparing' : 'Slet depot'}
          message={
            confirmDelete.kind === 'goal'
              ? `Slet opsparingen "${confirmDelete.name}"?`
              : `Slet depotet "${confirmDelete.name}"?`
          }
          danger
          onConfirm={performDelete}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
